import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Form, Input, Select, message, Divider, Checkbox } from "antd";
import YmsFooterForm from "components/footerForm";
import Layout from "components/layout";
import { bindActionCreators } from "redux";
import { Creators as LocationsActions } from "store/location/location-duck";
import { ILocationActiveData } from "store/location/location-model";
import { Creators as UserSessionActions } from "store/user-session/user-session-duck";
import { IUserData, MenuItemActive } from "store/user-session/user-session-model";
import { Creators as UsersActions } from "store/users/users-duck";
import { IRolesData, IEditUser } from "store/users/users-model";
import { validationLetter } from "utils/form/validations";
import { languageOptions, mapSelect, mapSelectObject, parseLocation, validationCharacters } from "utils/functions";
import useTranslation from "utils/hooks/useTranslation";
import { ConfirmCancelEdit, ConfirmEdit } from "./confirm";
import { onConfirmEditUser } from "./edit-user.service";
import * as S from "./styled/index.styled";

interface IPropsScreenUsersForm {
  isLoading?: boolean;
  rolesList?: Array<IRolesData>;
  locationActiveList?: Array<ILocationActiveData>;
  editUser?: IEditUser;
  formViewOrEditModified?: boolean;
  editActive?: boolean;
  userData?: IUserData;
}
interface IDispatchProps {
  usersUpdateUserAsync?: (...args) => void;
  usersUpdateFormModified?: (...args) => void;
  usersEditActive?: (...args) => void;
  userSessionMenuItemActive?: (...args) => void;
}

type IProps = IPropsScreenUsersForm & IDispatchProps;

type LayoutType = Parameters<typeof Form>[0]["layout"];

const UsersForm = ({
  rolesList,
  locationActiveList,
  editUser,
  formViewOrEditModified,
  isLoading,
  userData,
  editActive,
  usersUpdateUserAsync,
  usersUpdateFormModified,
  usersEditActive,
  userSessionMenuItemActive,
}: IProps) => {
  const [form] = Form.useForm();
  const router = useNavigate();
  const { Option } = Select;
  const YES_MESSAGE = useTranslation({ id: "yes" });
  const NO_MESSAGE = useTranslation({ id: "no" });
  const STATUS = useTranslation({ id: "status" });
  const ACTIVE = useTranslation({ id: "active" });
  const INACTIVE = useTranslation({ id: "inactive" });
  const TITLE_PAGE_EDIT_USER_REGISTRATION = useTranslation({
    id: "user_edit",
  });
  const REQUIRED_FIELDS_MESSAGE = useTranslation({
    id: "required_fields_are_required",
  });
  const [formLayout, setFormLayout] = useState<LayoutType>("vertical");
  const [locationActive, setLocationActive] = useState(editUser.locationDescription);
  const [multiLocationsList, setMultiLocationsList] = useState([]);
  const [multiLocationsValue, setMultiLocationsValue] = useState([]);
  const [visibleMultiLocations, setVisibleMultiLocations] = useState(false);

  const onFormLayoutChange = ({ layout }: { layout: LayoutType }) => {
    setFormLayout(layout);
  };
  const setMenuItemActive = () => userSessionMenuItemActive(MenuItemActive.USERS);

  const validation = (e) => {
    validationCharacters(e, validationLetter);
  };

  useEffect(() => {
    if (editUser?.id) {
      form.resetFields();
      setMultiLocationsValue([]);
      setVisibleMultiLocations(false);
      form.setFieldsValue(editUser);
    }
  }, [editUser]);

  const handleOnConfirmEditUser = () => {
    onConfirmEditUser({
      form,
      message,
      messageDescription: REQUIRED_FIELDS_MESSAGE,
      editUser,
      usersUpdateUserAsync,
      rolesList,
      locationActiveList,
      multiLocationsValue,
      visibleMultiLocations,
    });
  };

  const handleCloseEditUser = () => {
    !formViewOrEditModified && usersEditActive(false);
    setMenuItemActive();
  };

  useEffect(() => {
    editActive === false && router("/private/user");
  }, [editActive]);

  const onChangeActiveLocation = (value) => {
    const locationSelected = parseLocation(value);
    setLocationActive(locationSelected.description);
    const updatedList = locationActiveList.filter((item) => item.id !== locationSelected.id);
    setMultiLocationsList(updatedList);
    setMultiLocationsValue([]);
    form.resetFields(["multiLocation"]);
  };

  const onChangeCheckMultiLocations = (e) => {
    const isCheckMultiLocations = e.target.checked;
    setVisibleMultiLocations(isCheckMultiLocations);
    if (isCheckMultiLocations) {
      form.resetFields(["multiLocation"]);
      setMultiLocationsList([]);
      const updatedList = locationActiveList.filter((item) => item.description !== locationActive);
      setMultiLocationsList(updatedList);
      setMultiLocationsValue([]);
    }
  };

  useEffect(() => {
    if (editUser?.locationDescription) {
      setLocationActive(editUser?.locationDescription);
    }
  }, [editUser?.locationDescription]);

  useEffect(() => {
    if (editUser?.allowMultipleLocations) {
      setVisibleMultiLocations(true);
      form.setFieldValue(
        "multiLocation",
        editUser.locations.map((location) => location.description)
      );
      setMultiLocationsValue(editUser.locations.map((location) => location.description));
      const locationActive = editUser.locationDescription;
      const updatedList = locationActiveList.filter((item) => item.description !== locationActive);
      setMultiLocationsList(updatedList);
    }
  }, [locationActiveList, editUser]);

  return (
    <Layout title={TITLE_PAGE_EDIT_USER_REGISTRATION} isLoading={isLoading} role={userData.role}>
      <Form.Provider
        onFormChange={(_, info) => {
          const { changedFields } = info;
          const changedFileldsSize = changedFields?.length || 0;
          if (changedFileldsSize === 1) {
            usersUpdateFormModified(true);
          }
        }}
      >
        <Form style={S.FormLayout} layout={"vertical"} form={form} initialValues={{ layout: formLayout }} onValuesChange={onFormLayoutChange} name="registers">
          <S.LayoutForm>
            <div className="item">
              <S.UserInfo>
                <S.FormItem className="name" name="name" label={`${useTranslation({ id: "name" })}`} style={S.DescriptionField}>
                  <S.DescriptionUser>{editUser.name}</S.DescriptionUser>
                </S.FormItem>
                <S.FormItem className="email" name="email" label="E-mail" style={S.DescriptionField}>
                  <S.DescriptionUser>{editUser.email}</S.DescriptionUser>
                </S.FormItem>
                <S.FormItem className="status" label={STATUS} style={S.DescriptionField}>
                  <S.DescriptionUser>{editUser?.active ? ACTIVE : INACTIVE}</S.DescriptionUser>
                </S.FormItem>
                <S.MultiLocations className="checkbox-locations">
                  <Checkbox onChange={onChangeCheckMultiLocations} checked={visibleMultiLocations} data-testid="checkMultiLocations">
                    {`${useTranslation({ id: "allow_multiple_locations" })}`}
                  </Checkbox>
                </S.MultiLocations>
                <S.FormItem
                  name="role"
                  className="role"
                  label={`${useTranslation({ id: "profile" })}*`}
                  style={S.DescriptionFormItem}
                  rules={[
                    {
                      required: true,
                      message: useTranslation({ id: "required_field" }),
                    },
                  ]}
                >
                  <Select size="large" style={S.SelectField} data-testid="selectRole">
                    {rolesList?.length > 0 && mapSelect(rolesList)}
                  </Select>
                </S.FormItem>

                <S.FormItem
                  name="locationDescription"
                  className="location"
                  label={`${useTranslation({ id: "unit" })}*`}
                  style={S.DescriptionFormItem}
                  rules={[
                    {
                      required: true,
                      message: useTranslation({ id: "required_field" }),
                    },
                  ]}
                >
                  <Select
                    showSearch
                    optionFilterProp="children"
                    filterOption={(input, option) => option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                    size="large"
                    style={S.SelectField}
                    onChange={(value) => onChangeActiveLocation(value)}
                    data-testid="selectLocation"
                  >
                    {locationActiveList?.length > 0 && mapSelectObject(locationActiveList)}
                  </Select>
                </S.FormItem>
                <S.FormItem
                  name="jobTitle"
                  className="jobTitle"
                  label={`${useTranslation({ id: "job_title" })}*`}
                  style={S.DescriptionField}
                  rules={[
                    {
                      required: true,
                      message: useTranslation({ id: "required_field" }),
                    },
                  ]}
                >
                  <Input style={S.InputJobTitle} maxLength={30} allowClear data-testid="jobTitleValidation" onKeyPress={(e) => validation(e)} />
                </S.FormItem>
                <S.FormItem
                  name="languageDescription"
                  className="language"
                  label={`${useTranslation({ id: "language" })}*`}
                  style={S.DescriptionFormItem}
                  rules={[
                    {
                      required: true,
                      message: useTranslation({ id: "required_field" }),
                    },
                  ]}
                >
                  <Select size="large" style={S.SelectField} data-testid="selectLanguage">
                    {mapSelect(languageOptions)}
                  </Select>
                </S.FormItem>

                <S.LocationsContainer hidden={!visibleMultiLocations}>
                  <S.FormItem
                    name="multiLocation"
                    className="locations"
                    label={`${useTranslation({ id: "units_available" })}*`}
                    style={S.DescriptionField}
                    rules={[
                      {
                        required: visibleMultiLocations,
                        message: useTranslation({ id: "required_field" }),
                      },
                    ]}
                  >
                    <Select
                      mode="multiple"
                      style={S.SelectField}
                      value={multiLocationsValue}
                      onChange={(newValue: string[]) => {
                        setMultiLocationsValue(newValue);
                      }}
                      maxTagCount="responsive"
                      data-testid="multiLocations"
                    >
                      {multiLocationsList?.length > 0 &&
                        multiLocationsList.map((item, index) => (
                          <Option key={`select-options-location-${index}`} value={item.description}>
                            {item.description}
                          </Option>
                        ))}
                    </Select>
                  </S.FormItem>
                </S.LocationsContainer>
              </S.UserInfo>
              <div>
                <Divider className="Divider" />
                <YmsFooterForm
                  showCancelButton={true}
                  confirmbuttontxt={useTranslation({ id: "save" })}
                  cancelbuttontxt={useTranslation({ id: "cancel" })}
                  showConfirmButton={true}
                  customrenderconfirmbutton={<ConfirmEdit handleOnConfirm={() => handleOnConfirmEditUser()} okText={YES_MESSAGE} cancelText={NO_MESSAGE} />}
                  customrendercancelbutton={
                    <ConfirmCancelEdit
                      handleOnConfirm={() => usersEditActive(false)}
                      okText={YES_MESSAGE}
                      cancelText={NO_MESSAGE}
                      formViewOrEditModified={formViewOrEditModified}
                      handleCloseEditUser={handleCloseEditUser}
                    />
                  }
                />
              </div>
            </div>
            <input data-testid="change-location" onChange={(e) => onChangeActiveLocation(e.target.value)} style={S.BtnDataTestid} />
          </S.LayoutForm>
        </Form>
      </Form.Provider>
    </Layout>
  );
};

const mapDispatchToActions = (dispatch: any) => bindActionCreators({ ...UsersActions, ...LocationsActions, ...UserSessionActions }, dispatch);

const mapStateToProps = (state) => ({
  isLoading: state.Users.isLoading,
  rolesList: state.Users.rolesList,
  locationActiveList: state.Location.locationActiveList,
  editUser: state.Users.editUser,
  formViewOrEditModified: state.Users.formViewOrEditModified,
  editActive: state.Users.editActive,
  userData: state.UserSession.userData,
});

export default connect(mapStateToProps, mapDispatchToActions)(UsersForm);
